import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FaSortUp, FaSortDown, FaDownload } from 'react-icons/fa';
import Pagination from '../components/Pagination';
import DateRangeFilter from '../components/DateRangeFilter';
import { fetchDownloadsPerDay } from '../services/api';
import { formatCompactNumber } from '../utils/formatters';
import '../components/DataTable.css';
import './DataPages.css';
import './Pages.css';

const toUTCDateStr = (date) => `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;

const getDefaultDateRange = () => {
  const end = new Date();
  const start = new Date();
  start.setUTCDate(start.getUTCDate() - 29);
  return {
    startDate: toUTCDateStr(start),
    endDate: toUTCDateStr(end)
  };
};

// Downloaded Books per Day: per-day download metrics (users, book/comic downloads).
const DownloadedBooks = () => {
  const [allItems, setAllItems] = useState([]);
  const [earliestDate, setEarliestDate] = useState(null);
  const [latestDate, setLatestDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [sortOrder, setSortOrder] = useState('DESC');
  const [startDate, setStartDate] = useState(getDefaultDateRange().startDate);
  const [endDate, setEndDate] = useState(getDefaultDateRange().endDate);
  const [exporting, setExporting] = useState(false);
  const pageSize = 100;

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await fetchDownloadsPerDay(startDate, endDate);
      setAllItems(result.items || []);
      setEarliestDate(result.earliestDate || null);
      setLatestDate(result.latestDate || null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const sortedItems = useMemo(() => {
    const sorted = [...allItems].sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return sortOrder === 'DESC' ? dateB - dateA : dateA - dateB;
    });
    return sorted;
  }, [allItems, sortOrder]);

  const totalCount = sortedItems.length;
  const totalPages = Math.ceil(totalCount / pageSize);
  const paginatedItems = sortedItems.slice((page - 1) * pageSize, page * pageSize);

  const toggleSortOrder = () => {
    setSortOrder(prev => prev === 'DESC' ? 'ASC' : 'DESC');
    setPage(1);
  };

  const handleDateApply = () => {
    setPage(1);
  };

  const handleDownloadCsv = async () => {
    try {
      setExporting(true);
      const header = 'Date,Users,Book Downloads,Comic Download Users,Comic Downloads\n';
      const rows = sortedItems.map(item => `${formatDate(item.date)},${item.totalUsers},${item.count},${item.comicDownloadUsers},${item.comicDownloads}`).join('\n');
      const csv = header + rows;
      const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const now = new Date();
      const timestamp = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}_${String(now.getHours()).padStart(2,'0')}h${String(now.getMinutes()).padStart(2,'0')}m${String(now.getSeconds()).padStart(2,'0')}s`;
      link.download = `Downloaded_Books_${timestamp}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('CSV export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Downloaded Books per Day</h1>
        </div>
      </div>

      <div className="data-page-content">
        {error && <div className="table-empty">Error: {error}</div>}

        {!error && (
          <>
            <div className="filters-container">
              <DateRangeFilter
                startDate={startDate}
                endDate={endDate}
                onStartDateChange={setStartDate}
                onEndDateChange={setEndDate}
                onApply={handleDateApply}
                earliestDate={earliestDate}
                latestDate={latestDate}
              />
            </div>

            <div className="data-page-controls">
              <button className="btn-primary" onClick={handleDownloadCsv} title="Download CSV" disabled={exporting}>
                <FaDownload size={14} />
                <span>{exporting ? 'Exporting ...' : 'CSV'}</span>
              </button>

              {totalCount > 0 && (
                <div className="control-badge" style={{ marginLeft: 'auto' }}>
                  <span className="badge-label">Viewing</span>
                  <span className="badge-value">{formatCompactNumber(totalCount)} days</span>
                </div>
              )}
            </div>

            {loading && <div className="table-loading">Loading ...</div>}

            {!loading && paginatedItems.length > 0 && (
              <div className="data-page-table-card">
                <div className="data-table-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th className="sortable" onClick={toggleSortOrder}>
                          Day
                          <span className="sort-indicator">
                            {sortOrder === 'ASC' ? <FaSortUp size={12} /> : <FaSortDown size={12} />}
                          </span>
                        </th>
                        <th>Users</th>
                        <th>Book Downloads</th>
                        <th>Comic Download Users</th>
                        <th>Comic Downloads</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedItems.map((item) => (
                        <tr key={item.date}>
                          <td>{formatDate(item.date)}</td>
                          <td>{item.totalUsers.toLocaleString()}</td>
                          <td>{item.count.toLocaleString()}</td>
                          <td>{item.comicDownloadUsers.toLocaleString()}</td>
                          <td>{item.comicDownloads.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <Pagination
                  page={page}
                  totalPages={totalPages}
                  totalCount={totalCount}
                  pageSize={pageSize}
                  onPageChange={setPage}
                />
              </div>
            )}

            {!loading && totalCount === 0 && (
              <div className="table-empty">
                {startDate || endDate
                  ? 'No results found for the selected date range.'
                  : 'No download data found.'}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DownloadedBooks;
